/**
 * PF Revamp — typed event catalogue.
 *
 * All revamp-native events share the `pf_revamp.` prefix for easy filtering
 * in Firebase / Mixpanel / Intercom. The dispatcher (`usePfRevampAnalytics`)
 * is the single fan-out point for these events.
 *
 * Legacy parity events are separately enumerated in `legacyEvents.ts` and
 * fired alongside the new namespace where the revamp tree has a 1:1 code
 * analog — this keeps the §11 funnel regression gate honest at cutover.
 *
 * Naming conventions:
 *   - `pf_revamp.<noun>.<verb_past_tense>` (`product_drawer_opened`,
 *     `cart_item_added`, `draft_saved`, …).
 *   - Payloads are plain, flat objects with primitive values — Firebase
 *     requires ≤ 100 parameters per event and ≤ 100-char param values, so
 *     this shape is safe across all downstream destinations.
 */

import type { CartItemId, ConfigurationFlowId, SalesforceId } from 'models/purchaseFlowRevamp'
import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'
import type { SizeGateReason } from 'utils/purchaseFlow/segmentForSurface'

/** Event name constants. Exported so tests can reference without string drift. */
export const PfRevampEventName = {
  PRODUCT_DRAWER_OPENED: 'pf_revamp.product_drawer_opened',
  DRAWER_VARIANT_SELECTED: 'pf_revamp.drawer.variant_selected',
  CART_ITEM_ADDED: 'pf_revamp.cart_item_added',
  PACK_ADDED: 'pf_revamp.pack_added',
  STEP_ADVANCED: 'pf_revamp.step_advanced',
  STEP_RETURNED: 'pf_revamp.step_returned',
  PRODUCT_CONFIG_SAVED: 'pf_revamp.product_config_saved',
  STAGING_UPLOAD_CTA_CLICKED: 'pf_revamp.staging_upload_cta_clicked',
  ORDER_PLACED: 'pf_revamp.order_placed',
  PAYMENT_FAILED: 'pf_revamp.payment_failed',
  DRAFT_SAVED: 'pf_revamp.draft_saved',
  DRAFT_RESUMED: 'pf_revamp.draft_resumed',
  SESSION_RESTORED: 'pf_revamp.session_restored',
  ADMIN_TARGET_SET: 'pf_revamp.admin_target_set',
  SOURCE_IMAGE_QUALITY_CHECKED: 'pf_revamp.source_image_quality_checked',
  CREATIVE_CHOICE_ANNOUNCED: 'pf_revamp.creative_choice_announced',
  SCHEDULE_METHOD_SELECTED: 'pf_revamp.schedule_method_selected',
  STRIPE_REDIRECT_SUCCEEDED: 'pf_revamp.stripe_redirect.succeeded',
  STRIPE_REDIRECT_FAILED: 'pf_revamp.stripe_redirect.failed',
  FLOW_CLOSED: 'pf_revamp.flow_closed',
  FLOW_LOADED: 'pf_revamp.flow_loaded',
  SCREEN_ENTERED: 'pf_revamp.screen_entered',
  CONFIG_SCREEN_ENTERED: 'pf_revamp.config_screen_entered',
  ADDON_TOGGLED: 'pf_revamp.addon_toggled',
  CONFIG_PANEL_OPENED: 'pf_revamp.config_panel_opened',
  FLOOR_PLAN_CHOICE_MADE: 'pf_revamp.floor_plan_choice_made',
  LAND_REGISTER_SOURCE_CHOSEN: 'pf_revamp.land_register_source_chosen',
  CT_BOOKING_UNAVAILABLE: 'pf_revamp.ct_booking_unavailable',
  AI_EDITING_OPTION_SET: 'pf_revamp.ai_editing_option_set',
  ADDRESS_ENTRY_MODE_CHANGED: 'pf_revamp.address_entry_mode_changed',
  PROPERTY_CREATE_OPENED: 'pf_revamp.property_create_opened',
  PROPERTY_CREATE_SUBMITTED: 'pf_revamp.property_create_submitted',
  // Instructions step — the six in-step events legacy fired and v2 lost.
  MEETING_MODE_SELECTED: 'pf_revamp.meeting_mode_selected',
  MEETING_CONTACT_PREFILLED: 'pf_revamp.meeting_contact_prefilled',
  EXTRA_TOGGLED: 'pf_revamp.extra_toggled',
  CT_BOOKING_OPENED: 'pf_revamp.ct_booking_opened',
  CT_BOOKING_CHOSEN: 'pf_revamp.ct_booking_chosen',
  // Billing step.
  PAYMENT_METHOD_SELECTED: 'pf_revamp.payment_method_selected',
  CC_EMAIL_CHANGED: 'pf_revamp.cc_email_changed',
  STEP_ADVANCE_BLOCKED: 'pf_revamp.step_advance_blocked',
  CATALOGUE_SEARCHED: 'pf_revamp.catalogue_searched',
  // The CRM embed.
  PROPERTY_SIZE_SELECTED: 'pf_revamp.property_size_selected',
  SIZE_GATE_BLOCKED: 'pf_revamp.size_gate_blocked',
  CART_REPRICED: 'pf_revamp.cart_repriced',
  EMBED_OVERLAY_SHOWN: 'pf_revamp.embed_overlay_shown',
  EMBED_CONSENT_RECORDED: 'pf_revamp.embed_consent_recorded',
} as const
export type PfRevampEventName = typeof PfRevampEventName[keyof typeof PfRevampEventName]

export interface ProductDrawerOpenedPayload {
  shellProductId: SalesforceId
  cartItemId?: CartItemId
  mode: 'new' | 'edit'
}

export interface DrawerVariantSelectedPayload {
  shellProductId: SalesforceId
  variantId: SalesforceId
}

export interface CartItemAddedPayload {
  shellProductId: SalesforceId
  variantId: SalesforceId
  qty: number
  /** Total (unit * qty) as a 2dp-rounded number. Currency lives alongside. */
  priceTotal: number
  currency: string
}

export type PfStepName = 'catalogue' | 'configuration' | 'instructions' | 'billing' | 'summary'

interface StepNavPayload {
  fromStep: PfStepName
  toStep: PfStepName
}

export interface StepAdvancedPayload extends StepNavPayload {
  /** Leaving billing: how many CC recipients the order carries. */
  ccEmailsAddedCount?: number
  /** Leaving instructions: the client ticked that they meet the creative themselves; null when the meeting mode
   *  does not ask (legacy asked for an on-site meeting and a key pickup). */
  onSiteContactIsOrderer?: boolean | null
}

/**
 * A click on a Next (or Place order) that could not go on. `validationErrors` is a list, as legacy's
 * `validation_errors`: Mixpanel keeps it, the scalar-only destinations drop it.
 */
export interface StepAdvanceBlockedPayload extends StepNavPayload {
  validationErrors: NextStepValidationError[]
  /** Instructions only: the gap the page scrolled to and outlined, or null when none of them was on screen. */
  revealedGap?: NextStepValidationError | null
}

/** A settled catalogue search: how much it found, never the text typed. */
export interface CatalogueSearchedPayload {
  queryLength: number
  productMatches: number
  bundleMatches: number
}

export interface StepReturnedPayload extends StepNavPayload {
  /** Smart-back target — set on Instructions secondary when we route to a
   *  specific configuration tab instead of the generic Catalogue fallback.
   *  Lets us tell smart-back hits apart from generic-back in funnels. */
  targetTab?: { cartItemId: CartItemId; flowId: ConfigurationFlowId }
}

export interface ProductConfigSavedPayload {
  cartItemId: CartItemId
  flowId: ConfigurationFlowId
}

export interface StagingUploadCtaClickedPayload {
  cartItemId: CartItemId
}

/** A pack was chosen: its members went into the cart as `lineCount` ordinary lines. */
export interface PackAddedPayload {
  packProductCode: string
  lineCount: number
  priceTotal: number
  currency: string
}

export interface OrderPlacedPayload {
  paymentMethod: string
  orderId: string
  totalValue: number
  currency: string
  itemCount: number
  /** Admin orders only: the automation the admin skipped. */
  skipCtAutomation?: boolean
  skipExternalProviderSending?: boolean
}

/** A card or SEPA payment that did not go through on an order that already exists. */
export interface PaymentFailedPayload {
  orderId: string
  paymentMethod: string
  /** Stripe's error type, or `not_stripe` for a failure Stripe did not report. */
  type: string
  code: string | null
  declineCode: string | null
}

export interface DraftSavedPayload {
  draftId: string
  itemCount: number
}

export interface DraftResumedPayload {
  draftId: string
  /** Seconds since draft's `updatedAt`. -1 if unknown. */
  ageSeconds: number
  /** The step the draft was saved on, when it recorded one. */
  lastStep?: string
  /** The step the resume opened. */
  landedOn: string
}

export interface SessionRestoredPayload {
  /** Found on the server, not in this browser's drafts (another device; the cart is not restored). */
  source: 'server'
  /** The server knew it as ordered rather than open. */
  ordered: boolean
}

export interface AdminTargetSetPayload {
  targetUserId: string
  targetWorkspaceId: string
  /** The client's organization rules as stored on the target; `null` when unknown. */
  invoiceAllowed?: boolean | null
  requiresPoaRepresentatives?: boolean | null
  /** The automation the admin chose to skip for this order. */
  skipCtAutomation?: boolean
  skipExternalProviderSending?: boolean
}

/** The client chose fixed or flexible booking (not fired when a draft restores the choice). */
export interface ScheduleMethodSelectedPayload {
  method: 'fixed' | 'flexible'
}

/** A staging source drop was checked for size: what was refused, uploaded with a warning, or could not be read. */
export interface SourceImageQualityCheckedPayload {
  refusedCount: number
  lowQualityCount: number
  unreadableCount: number
}

export interface StripeRedirectSucceededPayload {
  paymentIntent: string
}

export interface StripeRedirectFailedPayload {
  paymentIntent: string | null
  status: string
}

export interface FlowClosedPayload {
  fromStep: PfStepName
}

/**
 * The flow finished loading and showed its first real screen — the funnel's
 * denominator. Everything else in the flow is measured against people who got
 * this far, so it fires once per mount, whatever the first screen turns out to
 * be, and never on a spinner. A first screen that is the embed's property error
 * still counts, as a failed load.
 */
export interface FlowLoadedPayload {
  /**
   * Which screen the client actually landed on. Usually `select_property` (the
   * root redirect) or `catalogue`, but a resumed draft or a bookmark can open
   * the flow on any step, and that is exactly the case worth being able to see.
   */
  firstScreen: 'select_property' | 'overlay' | PfStepName
  /** Set when the embed's CRM property could not be fetched and the error screen is what rendered. */
  failureReason?: 'PROPERTY_FETCH_FAILED'
}

/**
 * A step became the active screen — by navigation OR by being the one the flow
 * opened on. Legacy had no such event: its on-enter names rode the navigation,
 * so the entry step was never counted and the funnel had no top. Firing this
 * from the arriving step instead is what closes that hole; the navigation events
 * therefore no longer carry the destination's on-enter name.
 */
export interface ScreenEnteredPayload {
  step: PfStepName
  /** Billing and summary: the Next / Place order button was in view; null when it could not be measured. */
  ctaInViewport?: boolean | null
  /** Billing and summary: the page is taller than the window; null when it could not be measured. */
  stepContentOverflows?: boolean | null
  /** Billing: the invoice card's state; null when the options failed or never settled. */
  invoiceOption?: InvoiceOption | null
}

/**
 * The invoice card on billing: free and selectable, priced, shown free but locked (no subscription and no
 * invoice permission), or not offered at all. Legacy's `invoice_option` values.
 */
export type InvoiceOption = 'waived' | 'paid' | 'locked' | 'not_offered'

/**
 * One configuration surface became visible. In legacy each of these was a STEP
 * of its own and the corresponding `enters_order_*_page_screen` fired as the
 * client walked through them; v2 shows the same surfaces as tabs and pages of a
 * single configuration step, so the event fires on activation instead of on
 * navigation. What is counted — "this client reached the floor-plan form" — is
 * the same on both sides.
 */
export interface ConfigScreenEnteredPayload {
  cartItemId: CartItemId
  flowId: ConfigurationFlowId
}

/**
 * The panels a certificate configurator is divided into. The two certificates
 * declare their own enums and share four of these; the names are the enum
 * values, so a panel added on either side surfaces here as an unmapped id
 * rather than as a silently miscounted one.
 */
export type ConfigPanelId =
  | 'PROPERTY_INFORMATION'
  | 'BUILDING_LAYOUT'
  | 'CONSTRUCTION_METHOD'
  | 'ENERGY_AND_HEATING'
  | 'ADDITIONAL_INFORMATION'
  | 'HEATING_SYSTEM'
  | 'CONSUMPTION_INFORMATION'

/**
 * One panel inside a configuration form became visible.
 *
 * The energy certificates are the longest forms in the flow and the only place
 * legacy measured progress WITHIN a step, through its `opens_*` events. Without
 * these, a client who abandons halfway through a certificate is indistinguishable
 * from one who never opened it.
 */
export interface ConfigPanelOpenedPayload {
  cartItemId: CartItemId
  flowId: ConfigurationFlowId
  panel: ConfigPanelId
}

/**
 * The favourite-creative picker was opened and had nothing to offer.
 *
 * The one place the flow can dead-end on data rather than on the client: they
 * ask to pick a creative and the list comes back empty or broken. Without it,
 * that abandonment is indistinguishable from changing their mind.
 */
export interface CtBookingUnavailablePayload {
  reason: 'request_failed' | 'empty_list'
  /** Whether this was the list as first offered, or the result of a name search
   *  — an empty search result is a normal outcome, an empty first list is not. */
  trigger: 'initial' | 'refine'
}

/** An AI editing option was set. Today that is sky replacement and its type. */
export interface AiEditingOptionSetPayload {
  cartItemId: CartItemId
  skyReplacement: boolean
  skyType?: string | null
}

/**
 * The client switched between picking an address from search and typing it in.
 * Manual entry means the property could not be found, which is a data-quality
 * signal as much as a funnel one.
 */
export interface AddressEntryModeChangedPayload {
  manual: boolean
}

/** Where the client asked to create a property: the no-match row of the search, or the link under it. */
export type PropertyCreateSource = 'no_results' | 'link'

/**
 * The create-property form opened. The property search text carries over as the form's address
 * query; its length is sent, never the text, because it is usually an address.
 */
export interface PropertyCreateOpenedPayload {
  source: PropertyCreateSource
  prefilled: boolean
  queryLength: number
}

/** Where a property's band comes from: a picked area range, or the exact area the CRM sent. */
export type PropertySizeSource = 'range' | 'exact_area'

/**
 * A valid create-property form was submitted. `prefillKept` says the address was picked from the
 * suggestions for the carried-over query without editing it — the measure of whether the carry-over
 * saves the client retyping.
 */
export interface PropertyCreateSubmittedPayload {
  prefilled: boolean
  prefillKept: boolean
  manual: boolean
  /** The area band the property is created in. */
  areaRange: string | null
}

/**
 * How the client supplies their land-register sheets: picked off the parcel map,
 * or typed in by hand after declining the lookup.
 *
 * Legacy measured these as two unrelated events; they are one decision, and the
 * split between them is the interesting number — a client who types the sheets
 * by hand has refused a paid lookup.
 */
export interface LandRegisterSourceChosenPayload {
  cartItemId: CartItemId
  source: 'map' | 'manual'
  /** How many districts the map selection spans. Absent for manual entry. */
  districtCount?: number
}

/** The floor-plan configurator's choices that legacy measured one by one. */
export type FloorPlanChoice =
  | 'create_from_scratch'
  | 'template'
  | 'render_type'
  | 'color'
  | 'logo'

/**
 * One choice inside the floor-plan configurator.
 *
 * Legacy gave each of these its own event name, and they are kept — a funnel
 * built on them still works. The v2 event carries `choice` so the five can also
 * be read as one series, which is what makes "how far into the configurator do
 * clients get" answerable without joining five charts.
 */
export interface FloorPlanChoiceMadePayload {
  cartItemId: CartItemId
  choice: FloorPlanChoice
  /** The chosen value, where the choice has one (a colour, a render type, a
   *  template id). Absent for the choices that are an act rather than a value. */
  value?: string
  /** Which render type a template was applied under — legacy sent it alongside
   *  the template id and it is the axis that makes template counts readable. */
  renderType?: string
}

/**
 * Something offered alongside a product was ticked or unticked in the drawer.
 *
 * The drawer renders four such lists — two catalogue relations crossed with the
 * two ways Salesforce offers them — and all four ride ONE legacy name pair
 * (`option_selected` / `option_removed`), because to the funnel they are the
 * same act. `relation` and `role` are what keep the four separable, and they are
 * separate fields because they answer different questions: `relation` is how the
 * catalogue models it, `role` is what the client sees.
 */
export interface AddonToggledPayload {
  /** The product being configured — the parent, in legacy's terms. */
  shellProductId: SalesforceId
  /** The ticked product's own code. */
  productCode: string
  /** Which catalogue array it arrived on. */
  relation: 'add_on' | 'extra_service'
  /**
   * How Salesforce offers it (`Offer_In_Catalogue__c`): its own catalogue entry
   * sold beside the product, or an option OF the product. This is the axis to
   * segment on — `relation` cuts across it.
   */
  role: 'standalone' | 'main_product_option'
  /** The parent's `ProductKind`, when the catalogue publishes one. */
  parentKind?: string
  selected: boolean
}

/**
 * The organization `ProductKind` of the chosen meeting mode, not v2's own
 * `MeetingMode` — legacy's `organisation_method_selected` carried the kind, and
 * a renamed value would split the series at cutover.
 */
export interface MeetingModeSelectedPayload {
  organizationMethod: string
}

/**
 * The "I will be onsite" checkbox. `filled` is v2's addition: legacy fired its
 * event on tick AND untick with no way to tell them apart, so the legacy name
 * stays bidirectional for comparability while this carries the direction.
 */
export interface MeetingContactPrefilledPayload {
  organizationMethod: string
  filled: boolean
}

/**
 * An order-level extra toggled. Legacy sent an empty payload and named the event
 * after fast delivery whatever was toggled; `kind` is what makes the v2 series
 * answerable.
 */
export interface ExtraToggledPayload {
  kind: string
  selected: boolean
}

/** The favourite-creative picker opened, with the travel-cost range on offer. */
export interface CtBookingOpenedPayload {
  lowestTravelCost?: number
  highestTravelCost?: number
  currency?: string
  creativeCount: number
  /** Whether the order's workspace is on a paid plan; the choice is open to every plan. */
  isSubscribed?: boolean
}

/**
 * A payment method actually PICKED by the user — never the BE preselect, which
 * is a render-time value and would inflate the funnel with choices nobody made.
 */
export interface PaymentMethodSelectedPayload {
  paymentMethod: string
  isSubscribed: boolean
}

/**
 * A CC recipient added or removed. Legacy had two separate events and no
 * direction field; the bridge picks the legacy name from `added`.
 */
export interface CcEmailChangedPayload {
  added: boolean
}

/** A specific creative chosen from the picker. */
export interface CtBookingChosenPayload {
  creativeId: string
  creativeFullName?: string
  /** Formatted travel cost, as legacy sent it (amount + currency in one string). */
  travelCost?: string
  isSubscribed?: boolean
}

/**
 * The embed's order now has a size: the area band of the property the CRM opened it for. v2 has no band picker,
 * so it is the property's surface, read as it was or as the client entered it on the completion form.
 */
export interface PropertySizeSelectedPayload {
  /** Legacy's `Segment`: the band the surface falls in, null when the surface maps to none. */
  segment: string | null
  /** The surface came with the CRM's property. */
  sizePrefilled: boolean
  /** The client had to enter the size first. */
  gateShown: boolean
  /** Where the band came from: an exact surface or a picked area range; null when there is neither. */
  sizeSource: PropertySizeSource | null
}

/** The CRM's property has no usable surface, so the embed asks for it before the catalogue. */
export interface SizeGateBlockedPayload {
  reason: SizeGateReason
}

/**
 * A OneSystem cart came back from the session pricing, counted over its lines at the new prices: covered by the
 * free photo pool, at the partner discount, and charged now although the cart showed them free before.
 */
export interface CartRepricedPayload {
  coveredCount: number
  chargedTotal: number
  discountedCount: number
  swappedCount: number
}

export interface EmbedConsentRecordedPayload {
  consentTypes: string[]
}

/**
 * Discriminated union over every event the dispatcher accepts.
 * Additive changes are safe — exhaustive `switch` consumers will fail type
 * checks if they forget a new case.
 */
export type PfRevampEvent =
  | { type: typeof PfRevampEventName.PRODUCT_DRAWER_OPENED; payload: ProductDrawerOpenedPayload }
  | { type: typeof PfRevampEventName.DRAWER_VARIANT_SELECTED; payload: DrawerVariantSelectedPayload }
  | { type: typeof PfRevampEventName.CART_ITEM_ADDED; payload: CartItemAddedPayload }
  | { type: typeof PfRevampEventName.STEP_ADVANCED; payload: StepAdvancedPayload }
  | { type: typeof PfRevampEventName.STEP_RETURNED; payload: StepReturnedPayload }
  | { type: typeof PfRevampEventName.PRODUCT_CONFIG_SAVED; payload: ProductConfigSavedPayload }
  | { type: typeof PfRevampEventName.STAGING_UPLOAD_CTA_CLICKED; payload: StagingUploadCtaClickedPayload }
  | { type: typeof PfRevampEventName.PACK_ADDED; payload: PackAddedPayload }
  | { type: typeof PfRevampEventName.ORDER_PLACED; payload: OrderPlacedPayload }
  | { type: typeof PfRevampEventName.PAYMENT_FAILED; payload: PaymentFailedPayload }
  | { type: typeof PfRevampEventName.DRAFT_SAVED; payload: DraftSavedPayload }
  | { type: typeof PfRevampEventName.DRAFT_RESUMED; payload: DraftResumedPayload }
  | { type: typeof PfRevampEventName.ADMIN_TARGET_SET; payload: AdminTargetSetPayload }
  | { type: typeof PfRevampEventName.SOURCE_IMAGE_QUALITY_CHECKED; payload: SourceImageQualityCheckedPayload }
  | { type: typeof PfRevampEventName.CREATIVE_CHOICE_ANNOUNCED; payload: Record<string, never> }
  | { type: typeof PfRevampEventName.SCHEDULE_METHOD_SELECTED; payload: ScheduleMethodSelectedPayload }
  | { type: typeof PfRevampEventName.SESSION_RESTORED; payload: SessionRestoredPayload }
  | { type: typeof PfRevampEventName.STRIPE_REDIRECT_SUCCEEDED; payload: StripeRedirectSucceededPayload }
  | { type: typeof PfRevampEventName.STRIPE_REDIRECT_FAILED; payload: StripeRedirectFailedPayload }
  | { type: typeof PfRevampEventName.FLOW_CLOSED; payload: FlowClosedPayload }
  | { type: typeof PfRevampEventName.FLOW_LOADED; payload: FlowLoadedPayload }
  | { type: typeof PfRevampEventName.SCREEN_ENTERED; payload: ScreenEnteredPayload }
  | { type: typeof PfRevampEventName.CONFIG_SCREEN_ENTERED; payload: ConfigScreenEnteredPayload }
  | { type: typeof PfRevampEventName.ADDON_TOGGLED; payload: AddonToggledPayload }
  | { type: typeof PfRevampEventName.CONFIG_PANEL_OPENED; payload: ConfigPanelOpenedPayload }
  | { type: typeof PfRevampEventName.FLOOR_PLAN_CHOICE_MADE; payload: FloorPlanChoiceMadePayload }
  | { type: typeof PfRevampEventName.LAND_REGISTER_SOURCE_CHOSEN; payload: LandRegisterSourceChosenPayload }
  | { type: typeof PfRevampEventName.CT_BOOKING_UNAVAILABLE; payload: CtBookingUnavailablePayload }
  | { type: typeof PfRevampEventName.AI_EDITING_OPTION_SET; payload: AiEditingOptionSetPayload }
  | { type: typeof PfRevampEventName.ADDRESS_ENTRY_MODE_CHANGED; payload: AddressEntryModeChangedPayload }
  | { type: typeof PfRevampEventName.PROPERTY_CREATE_OPENED; payload: PropertyCreateOpenedPayload }
  | { type: typeof PfRevampEventName.PROPERTY_CREATE_SUBMITTED; payload: PropertyCreateSubmittedPayload }
  | { type: typeof PfRevampEventName.MEETING_MODE_SELECTED; payload: MeetingModeSelectedPayload }
  | { type: typeof PfRevampEventName.MEETING_CONTACT_PREFILLED; payload: MeetingContactPrefilledPayload }
  | { type: typeof PfRevampEventName.EXTRA_TOGGLED; payload: ExtraToggledPayload }
  | { type: typeof PfRevampEventName.CT_BOOKING_OPENED; payload: CtBookingOpenedPayload }
  | { type: typeof PfRevampEventName.CT_BOOKING_CHOSEN; payload: CtBookingChosenPayload }
  | { type: typeof PfRevampEventName.PAYMENT_METHOD_SELECTED; payload: PaymentMethodSelectedPayload }
  | { type: typeof PfRevampEventName.CC_EMAIL_CHANGED; payload: CcEmailChangedPayload }
  | { type: typeof PfRevampEventName.STEP_ADVANCE_BLOCKED; payload: StepAdvanceBlockedPayload }
  | { type: typeof PfRevampEventName.CATALOGUE_SEARCHED; payload: CatalogueSearchedPayload }
  | { type: typeof PfRevampEventName.PROPERTY_SIZE_SELECTED; payload: PropertySizeSelectedPayload }
  | { type: typeof PfRevampEventName.SIZE_GATE_BLOCKED; payload: SizeGateBlockedPayload }
  | { type: typeof PfRevampEventName.CART_REPRICED; payload: CartRepricedPayload }
  | { type: typeof PfRevampEventName.EMBED_OVERLAY_SHOWN; payload: Record<string, never> }
  | { type: typeof PfRevampEventName.EMBED_CONSENT_RECORDED; payload: EmbedConsentRecordedPayload }
